import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Moment from 'moment';
import { toast } from 'react-toastify';

import { makeStyles, useTheme } from '@mui/styles';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import LinearProgress from '@mui/material/LinearProgress';

import DeleteIcon from '@mui/icons-material/Delete';
import FileIcon from '@mui/icons-material/InsertDriveFile';

import DateTimePicker from '../../../components/styled/datetimepicker';
import ConfirmationDialog from '../../../components/confirmationdlg';

import IncidentService from '../../../data/incident-service';
import Auth from '../../../data/auth-service';

const useStyles = makeStyles(theme => ({
    card: {
        maxWidth: 345,
    },
    primary: {
        color: theme.palette.primary.main,
    },
    secondary: {
        color: theme.palette.secondary.main,
    },
    borderLine: {
        borderTop: `1px solid ${theme.palette.primary.main}`,
        paddingTop: theme.spacing(2),
        paddingBottom: theme.spacing(2),
    },
    filename: {
        color: theme.palette.secondary.main,
        textDecoration: 'underline',
    },
    hideFileInput: {
        display: 'none',
    },
}));

const IncidentUploads = (props) => {
    const classes = useStyles();
    const theme = useTheme();
    const [incident, setIncident] = useState(props.incident);
    const incidentPerms = Auth.permissions.get('Incidents');
    const [readonly] = useState(!incidentPerms.write);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);

    useEffect(() => {
        setIncident(props.incident);
    }, [props.incident]);

    const [file, setFile] = useState({
        name: '',
        content: null,
    });
    const [details, setDetails] = useState({
        description: '',
        mitigation: '',
        timestamp: Moment(),
    });
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [selectedFile, setSelectedFile] = useState({
        link: '',
        timestamp: '',
        description: '',
        mitigation: '',
    });

    const handleFileBrowse = (e) => {
        setFile(e.target.files[[0]]);
    };

    const handleCancelUpload = () => {
        setFile({name: '', content: null});
        setDetails({
            description: '',
            mitigation: '',
            timestamp: Moment(),
        });
    };

    const handleUploadFile = () => {
        setUploading(true);
        IncidentService.uploadFile(incident.uuid, file, details, e => {
            if(e.loaded === e.total) {
                setUploadProgress(100);
            } else {
                setUploadProgress(Math.floor((e.loaded/e.total)*100));
            }
        }, r => {
            if(r.readyState === 4) {
                if(r.status === 200) {
                    toast.success('Mitigation file uploaded successfully');
                } else {
                    toast.error("Unable to upload mitigation file, please confirm the file isn't larger than 20MB");
                }
            }
            setTimeout(() => {
                props.onKick();
                handleCancelUpload();
                setUploading(false);
            }, 2000);
        });
        // .then(res => {
        //     // other pages need the latest (e.g. for logs)
        //     props.onKick();
        //     handleCancelUpload();
        //     toast.success('Mitigation file uploaded successfully');
        // }, err => {
        //     // when file is too large, Go will just close the connection causing a 502 Bad Gateway error.
        //     // this is intentional when request exceeds limit to prevent clients from accidentally or maliciously sending a large request and wasting server resources.
        //     // https://golang.org/pkg/net/http/#MaxBytesReader
        //     toast.error("Unable to upload mitigation file, please confirm that the file isn't larger than 100 MB");
        //     console.error(err);
        // });
    };

    const handleDownloadFile = (filename) => {
        IncidentService.downloadFile(incident.uuid, filename).then(res => {
            let a = document.createElement('a');
            a.target = '_blank';
            a.download = filename;
            a.href = URL.createObjectURL(res);
            a.click();
        }, err => {
            toast.error(`Unable to download mitigation file: ${err}`);
            console.error(err);
        });
    };

    const handleToggleDelete = (name) => {
        if (name) {
            setSelectedFile(name);
        } else {
            setSelectedFile('');
        }
        setDeleteOpen(!deleteOpen);
    };

    const handleDeleteFile = () => {
        IncidentService.deleteFile(props.incident.uuid, selectedFile.link).then(res => {
            props.onKick();
            handleToggleDelete();
        }, err => {
            console.error(err);
            toast.error(`Unable to delete mitigation file: ${err}`);
        });
    };

    const handleUpdateDate = date => {
        setDetails({
            ...details,
            timestamp: date || Moment()
        });
    };

    const handleUpdateDescription = (e) => {
        setDetails({
            ...details,
            description: e.target.value,
        });
    };

    const handleUpdateMitigation = (e) => {
        setDetails({
            ...details,
            mitigation: e.target.value,
        });
    };

    return (
        <Box mt={2}>
            {!readonly && <Paper elevation={3}>
                <Box p={3}>
                    <Box display="flex" justifyContent="space-between">
                        <Typography variant="h6" className={classes.primary}>Upload a File</Typography>
                        <Box display={uploading ? 'flex' : 'none'} width="70%" mr={3} alignItems="center">
                            <Box width="100%" mr={1}>
                                <LinearProgress variant="determinate" value={uploadProgress} />
                            </Box>
                            <Box minWidth={35}>
                                <Typography variant="body2" color="textSecondary">
                                    {uploadProgress}%
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                    <Box mt={2}>
                        <TextField
                            variant="outlined"
                            value={file.name}
                            fullWidth
                            disabled
                            InputProps={{
                                endAdornment: (
                                    <label htmlFor="ir-file-upload">
                                        <Button
                                            variant="contained"
                                            color="secondary"
                                            aria-label="upload file"
                                            component="span"
                                        >
                                            Browse
                                        </Button>
                                    </label>
                                ),
                            }}
                        />
                        <input
                            id="ir-file-upload"
                            className={classes.hideFileInput}
                            type="file"
                            onClick={e => {e.target.value = null;}}
                            onChange={handleFileBrowse}
                        />
                    </Box>
                    <Box mt={2}>
                        {/* <Accordion expanded={Boolean(file.name)}> */}
                        <Accordion expanded={Boolean(file.name)}>
                            <AccordionSummary style={{ display: 'none' }}></AccordionSummary>
                            <AccordionDetails style={{ paddingRight: '0px', marginRight: '-16px' }}>
                                <Box display="block" width="100%" ml={-2} p={3}>
                                    <Box display="flex" justifyContent="flex-end">
                                        <DateTimePicker
                                            ampm={false}
                                            label="Date"
                                            aria-label="date"
                                            value={Moment(details.timestamp)}
                                            onChange={handleUpdateDate}
                                            inputFormat={theme.date.short}
                                            disableMaskedInput={true}
                                            maxDate={Moment()}
                                            showTodayButton={true}
                                        />
                                    </Box>
                                    <Box mt={2} display="flex" justifyContent="space-between">
                                        <Box width="100%" mr={2}>
                                            <TextField
                                                fullWidth
                                                multiline
                                                label="Description"
                                                value={file.description}
                                                onChange={handleUpdateDescription}
                                            />
                                        </Box>
                                        <Box width="100%" ml={2}>
                                            <TextField
                                                fullWidth
                                                multiline
                                                label="Mitigation"
                                                value={file.mitigation}
                                                onChange={handleUpdateMitigation}
                                            />
                                        </Box>
                                    </Box>
                                    <Box mt={2} display="flex" justifyContent="flex-end">
                                        <Box ml={2}><Button variant="contained" color="secondary" onClick={handleCancelUpload}>Cancel</Button></Box>
                                        <Box ml={2}><Button variant="contained" color="primary" onClick={handleUploadFile} disabled={!file || (file && !file.name)}>Upload</Button></Box>
                                    </Box>
                                </Box>
                            </AccordionDetails>
                        </Accordion>
                    </Box>
                </Box>
            </Paper>}
            <Box mt={4} px={1} mb={5}>
                <Grid container spacing={2} justifyContent="center">
                    <Grid container>
                        <Grid item xs={3}>
                            <Typography variant="h6" align="center" className={classes.primary}>Name</Typography>
                        </Grid>
                        <Grid item xs={2}>
                            <Typography variant="h6" align="center" className={classes.primary}>Date</Typography>
                        </Grid>
                        <Grid item xs={3}>
                            <Typography variant="h6" align="center" className={classes.primary}>Description</Typography>
                        </Grid>
                        <Grid item xs={3}>
                            <Typography variant="h6" align="center" className={classes.primary}>Mitigations</Typography>
                        </Grid>
                        <Grid item xs={1}>
                            <Typography variant="h6" align="center" className={classes.primary}>Actions</Typography>
                        </Grid>
                    </Grid>
                    {incident.data.files.map((v, i) => {
                        return (
                            <Grid key={'file-' + i} container className={classes.borderLine}>
                                <Grid item xs={3}>
                                    <Typography variant="h6">
                                        <Box display="flex">
                                            <Box pl={2} className={classes.primary}><FileIcon /></Box>
                                            <Box ml={2} className={classes.filename} style={{wordBreak: 'break-word'}}>
                                                <Button color="secondary" style={{textTransform: 'none'}} onClick={() => { handleDownloadFile(v.link) }}>{v.link}</Button>
                                            </Box>
                                        </Box>
                                    </Typography>
                                </Grid>
                                <Grid item xs={2}>
                                    <Typography align="center" className={classes.primary}>{Moment(v.timestamp).format(theme.date.short)}</Typography>
                                </Grid>
                                <Grid item xs={3}>
                                    <Typography align="center" variant="body1">{v.description}</Typography>
                                </Grid>
                                <Grid item xs={3}>
                                    <Typography align="center" className={classes.primary}>{v.mitigation}</Typography>
                                </Grid>
                                {!readonly && <Grid item xs={1}>
                                    <IconButton size="small" color="secondary" onClick={() => { handleToggleDelete(v) }}>
                                        <DeleteIcon />
                                    </IconButton>
                                </Grid>}
                            </Grid>
                        );
                    })}
                </Grid>
            </Box>
            <ConfirmationDialog
                open={deleteOpen}
                title="Delete File"
                message={(() => {
                    return (
                        <Box>
                            Are you sure you want to delete the file <span className={classes.secondary}>{selectedFile.link}</span>?
                        </Box>
                    );
                })()}
                cancelLabel="Cancel"
                okLabel="Yes"
                onClose={handleToggleDelete}
                onOK={handleDeleteFile}
            />
        </Box>
    );
};

IncidentUploads.propTypes = {
    incident: PropTypes.object.isRequired,
    onKick: PropTypes.func.isRequired
};

export default IncidentUploads;
